/**
 * <s-pdp-slider data-autoplay="MS"> — product page image slider.
 *
 * Markup contract:
 *  - [data-slider-track]: horizontal scroll-snap container; its [data-slide] children are the slides.
 *  - [data-slider-prev] / [data-slider-next]: arrow buttons (disabled at the ends).
 *  - [data-slider-current]: text node for the 1-based current slide number.
 *  - [data-slider-bar]: one progress line per slide (mobile).
 *  - [data-slider-thumb="INDEX"]: thumbnail buttons; the active one gets aria-current="true".
 *  - [data-slider-pause]: toggles autoplay (aria-pressed="true" when stopped).
 * Swiping is native scrolling. Autoplay advances every data-autoplay ms and loops; it pauses
 * on hover, focus and touch, skips while the page is hidden, and is off for reduced motion.
 */
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class PdpSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-slide]')];
    this.index = 0;
    this.paused = false;
    this.stopped = false;

    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.pause = () => { this.paused = true; };
    this.resume = () => { this.paused = false; };
    this.onTouchStart = () => {
      this.paused = true;
      clearTimeout(this.resumeTimer);
    };
    this.onTouchEnd = () => {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(this.resume, 4000);
    };

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeyDown);
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);

    this.update();
    this.startAutoplay();
  }

  disconnectedCallback() {
    clearInterval(this.autoTimer);
    clearTimeout(this.resumeTimer);
    if (!this.track) return;
    this.track.removeEventListener('scroll', this.onScroll);
    this.track.removeEventListener('keydown', this.onKeyDown);
    this.track.removeEventListener('touchstart', this.onTouchStart);
    this.track.removeEventListener('touchend', this.onTouchEnd);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerenter', this.pause);
    this.removeEventListener('pointerleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.resume);
  }

  startAutoplay() {
    clearInterval(this.autoTimer);
    const delay = Number(this.dataset.autoplay || 0);
    if (!delay || this.slides.length < 2 || this.stopped || reducedMotion()) return;
    this.autoTimer = setInterval(() => {
      if (this.paused || document.hidden) return;
      this.goTo(this.index >= this.slides.length - 1 ? 0 : this.index + 1);
    }, delay);
  }

  goTo(index) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[target];
    if (!slide) return;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const left = rtl
      ? -(this.track.scrollWidth - slide.offsetLeft - slide.offsetWidth)
      : slide.offsetLeft - this.track.offsetLeft;
    this.track.scrollTo({ left, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  onScroll() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const next = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (next !== this.index) {
        this.index = next;
        this.update();
      }
    });
  }

  onClick(event) {
    const pauseButton = event.target.closest('[data-slider-pause]');
    if (pauseButton) {
      this.stopped = !this.stopped;
      pauseButton.setAttribute('aria-pressed', String(this.stopped));
      if (this.stopped) clearInterval(this.autoTimer);
      else this.startAutoplay();
      return;
    }
    if (event.target.closest('[data-slider-prev]')) return this.goTo(this.index - 1);
    if (event.target.closest('[data-slider-next]')) return this.goTo(this.index + 1);
    const thumb = event.target.closest('[data-slider-thumb]');
    if (thumb) this.goTo(Number(thumb.dataset.sliderThumb));
  }

  onKeyDown(event) {
    if (event.key === 'ArrowRight') this.goTo(this.index + 1);
    else if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
    else return;
    event.preventDefault();
  }

  update() {
    const last = this.slides.length - 1;
    const prev = this.querySelector('[data-slider-prev]');
    const next = this.querySelector('[data-slider-next]');
    if (prev) prev.disabled = this.index <= 0;
    if (next) next.disabled = this.index >= last;
    const current = this.querySelector('[data-slider-current]');
    if (current) current.textContent = String(this.index + 1);
    this.querySelectorAll('[data-slider-bar]').forEach((bar, i) => {
      bar.classList.toggle('bg-on-surface', i === this.index);
      bar.classList.toggle('bg-on-surface/20', i !== this.index);
    });
    const strip = this.querySelector('[data-slider-thumb]')?.closest('ul');
    this.querySelectorAll('[data-slider-thumb]').forEach((thumb, i) => {
      if (i === this.index) {
        thumb.setAttribute('aria-current', 'true');
        // Keep the active thumbnail visible by scrolling only the strip, never the page.
        if (strip) {
          const item = thumb.parentElement;
          const start = item.offsetLeft - strip.offsetLeft;
          if (start < strip.scrollLeft || start + item.offsetWidth > strip.scrollLeft + strip.clientWidth) {
            strip.scrollTo({ left: start, behavior: reducedMotion() ? 'auto' : 'smooth' });
          }
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-pdp-slider')) customElements.define('s-pdp-slider', PdpSlider);
